import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MassNotification, NotificationDocumentRecipientSettings } from '../../store/communications';
import { Document, Dataset, DocumentReader, FieldDataType, DatasetType } from '@app/modules/core/store/models';
import { DocumentDisplayNameService } from '@app/modules/core/services/document-display-name.service';

@Component({
    selector: 'topo-comms-notification-document-selection',
    templateUrl: './notification-document-selection.component.html',
    styleUrls: ['./notification-document-selection.component.scss'],
    standalone: false
})
export class NotificationDocumentSelectionComponent {
  @Input() notification: MassNotification;
  @Input() dataset: Dataset;
  @Input() document: Document;

  @Output() selectedRecipientsChanged = new EventEmitter<NotificationDocumentRecipientSettings>();
  @Output() recipientCountChanged = new EventEmitter<number>();

  notificationDocumentSelections: NotificationDocumentSelectionInfo[];
  documentRecipientCount: number = 0;

  constructor(
    private documentDisplayNameService: DocumentDisplayNameService,
  ) { }

  ngOnInit(): void {
    const notificationDocumentRecipients = this.notification?.recipientSettings?.notificationDocumentSettings;

    this.notificationDocumentSelections = [
      { dataset: this.dataset, document: this.document }
      ]
      .filter(({dataset, document}) => dataset?.type === DatasetType.Assets)
      .map(({ dataset, document }) => {
        const reader = new DocumentReader(document);
        return {
          documentUId : reader.uId,
          documentName : this.documentDisplayNameService.getDisplayName(dataset, reader),
          include : notificationDocumentRecipients?.documentUIds?.includes(reader.uId) ?? true,
          recipientCount : dataset.schema.fields.filter(f =>
            (f.dataType === FieldDataType.Email || f.dataType === FieldDataType.Phone)
            && f.settings?.useForCommunications !== false
            && reader.getFieldValue(f.systemName) != null
            ).length,
          isCustomIcon : dataset.isCustomIcon,
          iconPath : dataset.iconPath,
          color : dataset.defaultColor,
        }});

      this.emitSelectedRecipientsChanged();
      this.calculateTotalRecipients();
  }

  private calculateTotalRecipients() : void {
    this.documentRecipientCount = 0;
    this.notificationDocumentSelections.filter(s => s.include).forEach(s => this.documentRecipientCount += s.recipientCount);
    this.emitRecipientCountChanged();
  }

  get includeAllDocuments() {
    return !this.partialIncludeDocuments && this.notificationDocumentSelections?.some(a => a.include);
  }

  get partialIncludeDocuments() {
    return (
      this.notificationDocumentSelections.some(a => a.include) && !this.notificationDocumentSelections.every(a => a.include));
  }

  private emitSelectedRecipientsChanged(): void {
    const notificationDocumentRecipientSettings = {
      documentUIds: this.notificationDocumentSelections.filter(s => s.include).map(s => s.documentUId),
    };
    this.selectedRecipientsChanged.emit(notificationDocumentRecipientSettings);
  }

  private emitRecipientCountChanged(): void {
    this.recipientCountChanged.emit(this.documentRecipientCount);
  }

  onIncludeDocumentsChanged(event: any): void {
    event?.stopPropagation();

    const selectedState = this.includeAllDocuments;
    this.notificationDocumentSelections.forEach(s => s.include = !selectedState);

    this.calculateTotalRecipients();
    this.emitSelectedRecipientsChanged();
    this.emitRecipientCountChanged();
  }

  onDocumentSelectChanged(documentSelection: NotificationDocumentSelectionInfo, event: any) {
    event?.stopPropagation();

    documentSelection.include = !documentSelection.include;

    this.calculateTotalRecipients();
    this.emitSelectedRecipientsChanged();
    this.emitRecipientCountChanged();
  }
}

interface NotificationDocumentSelectionInfo
{
  documentUId : string;
  documentName: string,
  include : boolean,
  recipientCount : number,
  isCustomIcon : boolean,
  iconPath? : string,
  color: string;
}

